import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ServiceStatus } from "@/models/enums/service";
import { mergeObjects } from "@/utils/utils";

import Service from "@/services";
import { MasterMaterialModel } from "@/models/classes/event/master_material";
import { EventDetailModel } from "@/models/classes/event/event_detail";
import { TechnicianModel } from "@/models/classes/event/technician";
import {
  OnEventModel,
  UpdateOnEventDetailModel,
  UpdateOnEventInstallationModel,
} from "@/models/classes/event/on_event";
import { EventDetailInstallModel } from "@/models/classes/event/event_detail_install";

const service = new Service.EventService();

interface EventState {
  // COMMON
  masterMaterial: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data: MasterMaterialModel[];
    total: number;
    page: number;
    limit: number;
  };
  technicians: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data: TechnicianModel[];
    total: number;
  };

  // PRE-EVENT
  preEvent: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data: EventDetailModel | null;
  };

  // ON-EVENT
  onEvent: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data: OnEventModel | null;
  };
  onEventPaymentDetail: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data: EventDetailModel | null;
  };
  onEventInstallationDetail: {
    network: {
      code: ServiceStatus;
      error?: string;
    };
    data: EventDetailInstallModel | null;
  };

  // POST-EVENT
}

const initialState: EventState = {
  // COMMON
  masterMaterial: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: [],
    total: 0,
    page: 1,
    limit: 20,
  },
  technicians: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: [],
    total: 0,
  },

  // PRE-EVENT
  preEvent: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: null,
  },

  // ON-EVENT
  onEvent: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: null,
  },
  onEventPaymentDetail: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: null,
  },
  onEventInstallationDetail: {
    network: {
      code: ServiceStatus.idle,
      error: undefined,
    },
    data: null,
  },

  // POST-EVENT
};

export const slice = createSlice({
  name: "EventState",
  initialState,
  reducers: {
    reset: () => initialState,
    patch: (state, action) => mergeObjects({ ...state }, action.payload),
  },
  extraReducers: (builder) => {
    // COMMON
    builder.addCase(onFetchMasterMaterial.pending, (state) => {
      state.masterMaterial.network.code = ServiceStatus.loading;
      state.masterMaterial.data = [];
      state.masterMaterial.total = 0;
    });
    builder.addCase(onFetchMasterMaterial.fulfilled, (state, action) => {
      state.masterMaterial.network.code = ServiceStatus.succeeded;
      if (action.payload) {
        state.masterMaterial.data = action.payload.data;
        state.masterMaterial.total = action.payload.total;
      }
    });
    builder.addCase(onFetchMasterMaterial.rejected, (state, action) => {
      state.masterMaterial.network.code = ServiceStatus.failed;
      state.masterMaterial.network.error =
        action.error.message || "Failed to fetch master material";
    });

    builder.addCase(onFetchMoreMasterMaterial.pending, (state) => {
      state.masterMaterial.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchMoreMasterMaterial.fulfilled, (state, action) => {
      state.masterMaterial.network.code = ServiceStatus.succeeded;
      if (action.payload) {
        state.masterMaterial.data = [
          ...state.masterMaterial.data,
          ...action.payload.data,
        ];
        state.masterMaterial.total = action.payload.total;
      }
    });
    builder.addCase(onFetchMoreMasterMaterial.rejected, (state, action) => {
      state.masterMaterial.network.code = ServiceStatus.failed;
      state.masterMaterial.network.error =
        action.error.message || "Failed to fetch more master material";
    });

    builder.addCase(onFetchTechnicians.pending, (state) => {
      state.technicians.network.code = ServiceStatus.loading;
      state.technicians.data = [];
      state.technicians.total = 0;
    });
    builder.addCase(onFetchTechnicians.fulfilled, (state, action) => {
      state.technicians.network.code = ServiceStatus.succeeded;
      if (action.payload) {
        state.technicians.data = action.payload.data || [];
        state.technicians.total = action.payload.total || 0;
      }
    });
    builder.addCase(onFetchTechnicians.rejected, (state, action) => {
      state.technicians.network.code = ServiceStatus.failed;
      state.technicians.network.error =
        action.error.message || "Failed to fetch technicians";
    });

    builder.addCase(onFetchMoreTechnicians.pending, (state) => {
      state.technicians.network.code = ServiceStatus.loading;
    });
    builder.addCase(onFetchMoreTechnicians.fulfilled, (state, action) => {
      state.technicians.network.code = ServiceStatus.succeeded;
      if (action.payload && action.payload.data) {
        state.technicians.data = [
          ...state.technicians.data,
          ...action.payload.data,
        ];
        state.technicians.total = action.payload.total || state.technicians.total;
      }
    });
    builder.addCase(onFetchMoreTechnicians.rejected, (state, action) => {
      state.technicians.network.code = ServiceStatus.failed;
      state.technicians.network.error =
        action.error.message || "Failed to fetch more technicians";
    });

    // PRE-EVENT

    builder.addCase(onFetchPreEventDetail.pending, (state) => {
      state.preEvent.network.code = ServiceStatus.loading;
      state.preEvent.data = null;
    });
    builder.addCase(onFetchPreEventDetail.fulfilled, (state, action) => {
      state.preEvent.network.code = ServiceStatus.succeeded;
      if (action.payload && action.payload.data) {
        state.preEvent.data = action.payload.data;
      }
    });
    builder.addCase(onFetchPreEventDetail.rejected, (state, action) => {
      state.preEvent.network.code = ServiceStatus.failed;
      state.preEvent.network.error =
        action.error.message || "Failed to fetch pre-event detail";
    });

    builder.addCase(onUpdatePreEventDelivery.fulfilled, (state, action) => {
      if (state.preEvent.data && action.meta.arg?.payload) {
        const payload = action.meta.arg.payload;
        if (payload.eventDate !== undefined) {
          state.preEvent.data.eventDate = payload.eventDate;
        }
        if (payload.shiptoDate !== undefined) {
          state.preEvent.data.shiptoDate = payload.shiptoDate;
        }
        if (payload.startInstallDate !== undefined) {
          state.preEvent.data.startInstallDate = payload.startInstallDate;
        }
        if (payload.endInstallDate !== undefined) {
          state.preEvent.data.endInstallDate = payload.endInstallDate;
        }
        if (payload.shiptoAddress) {
          state.preEvent.data.shiptoAddress = payload.shiptoAddress;
        }
      }
    });

    // ON-EVENT
    builder.addCase(onFetchOnEvent.pending, (state) => {
      state.onEvent.network.code = ServiceStatus.loading;
      state.onEvent.data = null;
    });
    builder.addCase(onFetchOnEvent.fulfilled, (state, action) => {
      state.onEvent.network.code = ServiceStatus.succeeded;
      if (action.payload && action.payload.data) {
        state.onEvent.data = action.payload.data;
      }
    });
    builder.addCase(onFetchOnEvent.rejected, (state, action) => {
      state.onEvent.network.code = ServiceStatus.failed;
      state.onEvent.network.error =
        action.error.message || "Failed to fetch on-event";
    });

    builder.addCase(onFetchOnEventPaymentDetail.pending, (state) => {
      state.onEventPaymentDetail.network.code = ServiceStatus.loading;
      state.onEventPaymentDetail.network.error = undefined;
      state.onEventPaymentDetail.data = null;
    });
    builder.addCase(onFetchOnEventPaymentDetail.fulfilled, (state, action) => {
      state.onEventPaymentDetail.network.code = ServiceStatus.succeeded;
      if (action.payload?.result) {
        state.onEventPaymentDetail.data = action.payload.data;
      }
    });
    builder.addCase(onFetchOnEventPaymentDetail.rejected, (state, action) => {
      state.onEventPaymentDetail.network.code = ServiceStatus.failed;
      state.onEventPaymentDetail.network.error = action.payload as string;
    });

    builder.addCase(onFetchOnEventInstallationDetail.pending, (state) => {
      state.onEventInstallationDetail.network.code = ServiceStatus.loading;
      state.onEventInstallationDetail.network.error = undefined;
      state.onEventInstallationDetail.data = null;
    });
    builder.addCase(onFetchOnEventInstallationDetail.fulfilled, (state, action) => {
      state.onEventInstallationDetail.network.code = ServiceStatus.succeeded;
      if (action.payload?.result) {
        state.onEventInstallationDetail.data = action.payload.data;
      }
    });
    builder.addCase(onFetchOnEventInstallationDetail.rejected, (state, action) => {
      state.onEventInstallationDetail.network.code = ServiceStatus.failed;
      state.onEventInstallationDetail.network.error = action.payload as string;
    });

    // POST-EVENT
  },
});

const getErrorMessage = (error: any, fallbackMessage: string = "เกิดข้อผิดพลาด") => {
  return error?.response?.data?.displayMessage || fallbackMessage;
};

// COMMON
export const onFetchMasterMaterial = createAsyncThunk(
  "event/masterMaterial",
  async (
    args: {
      matType: string;
      searchText: string;
      matGroupId?: number;
      matBrandId?: number;
      page: number;
      limit: number;
    },
    thunkAPI,
  ) => {
    try {
      return await service.getEventMasterMaterial(
        args.matType,
        args.searchText,
        args.matGroupId,
        args.matBrandId,
        args.page,
        args.limit,
      );
    } catch (error: any) {
      return thunkAPI.rejectWithValue(getErrorMessage(error, "Failed to fetch master material"));
    }
  },
);

export const onFetchMoreMasterMaterial = createAsyncThunk(
  "event/moreMasterMaterial",
  async (
    args: {
      matType: string;
      searchText: string;
      matGroupId?: number;
      matBrandId?: number;
      page: number;
      limit: number;
    },
    thunkAPI,
  ) => {
    try {
      return await service.getEventMasterMaterial(
        args.matType,
        args.searchText,
        args.matGroupId,
        args.matBrandId,
        args.page,
        args.limit,
      );
    } catch (error: any) {
      return thunkAPI.rejectWithValue(getErrorMessage(error, "Failed to fetch more master material"));
    }
  },
);

export const onFetchTechnicians = createAsyncThunk(
  "event/technicians",
  async (
    args: { eventId: number; searchText: string; offset?: number; limit?: number },
    thunkAPI,
  ) => {
    try {
      return await service.getTechnician(
        args.eventId,
        args.searchText,
        args.offset,
        args.limit,
      );
    } catch (error: any) {
      return thunkAPI.rejectWithValue(getErrorMessage(error, "Failed to fetch technicians"));
    }
  },
);

export const onFetchMoreTechnicians = createAsyncThunk(
  "event/moreTechnicians",
  async (
    args: { eventId: number; searchText: string; offset: number; limit: number },
    thunkAPI,
  ) => {
    try {
      return await service.getTechnician(
        args.eventId,
        args.searchText,
        args.offset,
        args.limit,
      );
    } catch (error: any) {
      return thunkAPI.rejectWithValue(getErrorMessage(error, "Failed to fetch more technicians"));
    }
  },
);

export const onUpdateEventApprove = createAsyncThunk(
  "event/approveEvent",
  async (
    args: { events: any[]; isApproved: boolean; rejectReason?: string },
    thunkAPI,
  ) => {
    try {
      return await service.approveEvent(args);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(getErrorMessage(error, "Failed to approve event"));
    }
  },
);

// PRE-EVENT
export const onFetchPreEventDetail = createAsyncThunk(
  "event/preEventDetail",
  async (eventId: number, thunkAPI) => {
    try {
      return await service.getPreEventDetail(eventId);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(getErrorMessage(error, "Failed to fetch pre-event detail"));
    }
  },
);

export const onUpdatePreEventCustomerAccept = createAsyncThunk(
  "event/preEventCustomerAccept",
  async (
    args: {
      eventId: number;
      payload: { isAccepted: boolean; reason?: string };
    },
    thunkAPI,
  ) => {
    try {
      return await service.customerPreEventAccept(args.eventId, args.payload);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(getErrorMessage(error, "Failed to customer accept"));
    }
  },
);

export const onUploadPreEventReceipt = createAsyncThunk(
  "event/preEventUploadReceipt",
  async (
    args: {
      eventId: number;
      payload: {
        imgUrls: string[];
        paymentType: string;
        depositAmount: number;
        note?: string;
        product?: any[];
      };
    },
    thunkAPI,
  ) => {
    try {
      return await service.uploadPreEventReceipt(args.eventId, args.payload);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(getErrorMessage(error, "Failed to upload receipt"));
    }
  },
);

export const onUpdatePreEventDelivery = createAsyncThunk(
  "event/updatePreEventDelivery",
  async (
    args: {
      eventId: number;
      payload: {
        eventDate?: string;
        shiptoDate?: string;
        startInstallDate?: string;
        endInstallDate?: string;
        shiptoAddress: any;
      };
    },
    thunkAPI,
  ) => {
    try {
      return await service.updatePreEventDelivery(args.eventId, args.payload);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        getErrorMessage(error, "Failed to update event delivery"),
      );
    }
  },
);

// ON-EVENT
export const onFetchOnEvent = createAsyncThunk(
  "event/onEvent",
  async (eventId: number, thunkAPI) => {
    try {
      return await service.getOnEvent(eventId);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(getErrorMessage(error, "Failed to fetch on-event"));
    }
  },
);

export const onFetchOnEventPaymentDetail = createAsyncThunk(
  "event/onEventPaymentDetail",
  async (eventId: number, thunkAPI) => {
    try {
      return await service.getOnEventPayment(eventId);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(getErrorMessage(error, "Failed to fetch on-event payment detail"));
    }
  },
);

export const onUpdateOnEventPayment = createAsyncThunk(
  "event/updateOnEventPayment",
  async (
    args: {
      eventId: number;
      payload: UpdateOnEventDetailModel;
    },
    thunkAPI,
  ) => {
    try {
      return await service.updateOnEventPayment(args.eventId, args.payload);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        getErrorMessage(error, "Failed to update on-event payment detail"),
      );
    }
  },
);

export const onFetchOnEventInstallationDetail = createAsyncThunk(
  "event/onEventInstallationDetail",
  async (eventId: number, thunkAPI) => {
    try {
      return await service.getOnEventInstallation(eventId);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        getErrorMessage(error, "Failed to fetch on-event installation detail"),
      );
    }
  },
);

export const onUpdateOnEventInstallation = createAsyncThunk(
  "event/updateOnEventInstallation",
  async (
    args: {
      eventId: number;
      payload: UpdateOnEventInstallationModel;
    },
    thunkAPI,
  ) => {
    try {
      return await service.updateOnEventInstallation(args.eventId, args.payload);
    } catch (error: any) {
      return thunkAPI.rejectWithValue(
        getErrorMessage(error, "Failed to update on-event installation detail"),
      );
    }
  },
);

// POST-EVENT

export const { reset, patch } = slice.actions;
export default slice.reducer;
